"use strict";

import dispatcher from "./../dispatcher";
import lang from "vwa/lang";
import createWellQuantity from "vwa/quantity/createWellQuantity";

import {pointToLatLng, positionToLatLng} from "./geo";
import {getNameOfPOI} from "./poi";
import {getUnitAssignment} from "./user";
import vehiclePOICrossingStore from "./../store/vehiclePOICrossings";
import displayedDetailsStore from "./../store/displayedDetails";
import openedDetailsStore from "./../store/openedDetails";
import poiCrossingStore from "./../store/vehiclePOICrossings";

export default {
	getVisibilityOfPOICrossings,
	getPOICrossingByTime,
	getBoundsOfPOICrossings,
	focusOnVehiclePOICrossings,
	focusOnVehiclePOICrossing,
	getResolvedSection,
	getResolvedSummary,
	getResolvedSummaryOfSummaries
};

export {getVisibilityOfPOICrossings as getVisibilityOfPOICrossings};
export {getPOICrossingByTime as getPOICrossingByTime};
export {getBoundsOfPOICrossings as getBoundsOfPOICrossings};
export {focusOnVehiclePOICrossings as focusOnVehiclePOICrossings};
export {focusOnVehiclePOICrossing as focusOnVehiclePOICrossing};
export {getResolvedSection as getResolvedSection};
export {getResolvedSummary as getResolvedSummary};
export {getResolvedSummaryOfSummaries as getResolvedSummaryOfSummaries};

function getVisibilityOfPOICrossings(vehicleId){
	const displayedDetails = displayedDetailsStore.get();
	const descriptor = openedDetailsStore.get(vehicleId);
	if (!displayedDetails || !descriptor)
		return false;
	if (displayedDetails.id == vehicleId && descriptor.activePage == "poiCrossings")
		return true;
	return false;
}

function getPOICrossingByTime(vehicleId, time){
	return poiCrossingStore.get(vehicleId).result.crossings.find(e => e.time == time);
}

function getBoundsOfPOICrossings(vehicleId){
	const descriptor = vehiclePOICrossingStore.get(vehicleId);
	if(!descriptor){
		return null;
	}
	
	const bounds = new google.maps.LatLngBounds();
	descriptor.result.crossings.forEach(
		crossing => {
			const latLng = pointToLatLng(crossing.location);
			bounds.extend(latLng);
		}
	);
	
	return bounds;
}

function focusOnVehiclePOICrossings(vehicleId){
	if(!getVisibilityOfPOICrossings(vehicleId)){
		return;
	}
	
	const descriptor = vehiclePOICrossingStore.get(vehicleId);
	if(!descriptor){
		return;
	}
	
	const bounds = getBoundsOfPOICrossings(vehicleId);
	if(!bounds || bounds.isEmpty()){
		return;
	}
	
	dispatcher.dispatch("setMap", {
		bounds: bounds.toJSON()
	});
}

function focusOnVehiclePOICrossing(vehicleId, time){
	if(!getVisibilityOfPOICrossings(vehicleId)){
		return;
	}
	
	const descriptor = vehiclePOICrossingStore.get(vehicleId);
	if(!descriptor){
		return;
	}
	
	const crossing = descriptor.result.crossings.find(crossing => crossing.time == time);
	if(!crossing){
		return;
	}
	
	dispatcher.dispatch("setMap", {
		center: pointToLatLng(crossing.location),
		zoom:	15
	});
}

function getFormattedTime(time) {
	const timeDiffHours = parseInt(time / 1000 / 60 / 60);
	const timeDiffMinutes = parseInt((time / 1000 / 60) % 60);
	if(timeDiffMinutes > 0){
		return (timeDiffHours > 0 ? timeDiffHours + " h " : "") + timeDiffMinutes + " m";
	}
	else{
		const timeDiffSeconds = parseInt(time / 1000);
		return (timeDiffSeconds + " s");
	}
}

function getDataString(data, application, representationType) {
	const wellQuantity = createWellQuantity(
		getUnitAssignment(),
		application,
		data.value,
		data.unit,
		representationType
	);
	return `${wellQuantity.value} ${wellQuantity.unit}`;
}

function getResolvedAggregatedTimeObjects(data){
	const formattedData = Object.assign({}, data);
	
	for(let key in formattedData){
		if(key != "unit"){
			formattedData[key] = getFormattedTime(formattedData[key]);
		} 
	}
	
	return formattedData;
}

function getResolvedAggregatedDataObjects(data, application, representationType){
	const resolvedData = Object.assign({}, data);
	
	for(let key in resolvedData){
		resolvedData[key] = getDataString({value: data[key], unit: data.unit}, application, representationType);
	}
	
	return resolvedData;
}

function getResolvedSection(rawSection) {
	return {
		start: {
			address:	rawSection.start.address || lang.UNKNOWN,
			time:		new Date(rawSection.start.time.value),
			timeStamp:	rawSection.start.time.value,
			position:	rawSection.start.location && rawSection.start.location.value ? pointToLatLng(rawSection.start.location.value).toJSON() : null,
			totalVehicleDistance: getDataString(rawSection.start.totalVehicleDistance, "distanceTraveled", "Float"),
			truncated:	rawSection.start.truncated
		},
		end: {
			address:	rawSection.end.address || lang.UNKNOWN,
			time:		new Date(rawSection.end.time.value),
			timeStamp:	rawSection.end.time.value,
			position:	rawSection.end.position && rawSection.end.position.value ? pointToLatLng(rawSection.end.position.value).toJSON() : null,
			totalVehicleDistance: getDataString(rawSection.end.totalVehicleDistance, "distanceTraveled", "Float"),
			truncated:	rawSection.end.truncated
		},
		starts:				rawSection.starts.value,
		stops:				rawSection.stops.value,
		ignitionTime:		getFormattedTime(rawSection.ignitionTime.value),
		nonIgnitionTime:	getFormattedTime(rawSection.nonIgnitionTime.value),
		poi:				getNameOfPOI(rawSection.poi),
		avgSpeed:			getDataString(rawSection.avgSpeed, "vehicleSpeed", "Integer"),
		maxSpeed:			getDataString(rawSection.maxSpeed, "vehicleSpeed", "Integer"),
		totalVehicleDistanceDiff:	getDataString(rawSection.totalVehicleDistanceDiff, "distanceTraveled", "Float"),
		timeDiff:			getFormattedTime(rawSection.timeDiff.value)
	};
}

function getResolvedSummary(rawSummary){
	return {
		arrivals:			rawSummary.arrivals.value,
		departures:			rawSummary.departures.value,
		ignitionTime:		getResolvedAggregatedTimeObjects(rawSummary.ignitionTime),
		speed:				getResolvedAggregatedDataObjects(rawSummary.speed, "vehicleSpeed", "Integer"),
		nonIgnitionTime:	getResolvedAggregatedTimeObjects(rawSummary.nonIgnitionTime),
		poi:				getNameOfPOI(rawSummary.poi),
		sectionCount:		rawSummary.sectionCount.value,
		starts:				rawSummary.starts,
		stops:				rawSummary.stops,
		timeDiff:			getResolvedAggregatedTimeObjects(rawSummary.timeDiff),
		totalVehicleDistanceDiff:	getResolvedAggregatedDataObjects(rawSummary.totalVehicleDistanceDiff, "distanceTraveled", "Float")
	}
}

function getResolvedSummaryOfSummaries(rawSummary){
	return {
		arrivals:			rawSummary.arrivals.value,
		departures:			rawSummary.departures.value,
		ignitionTime:		getFormattedTime(rawSummary.ignitionTime.value),
		nonIgnitionTime:	getFormattedTime(rawSummary.nonIgnitionTime.value),
		poiCount:			rawSummary.poiCount.value,
		starts:				rawSummary.starts.value,
		stops:				rawSummary.stops.value,
		timeDiff:			getFormattedTime(rawSummary.timeDiff.value),
		totalVehicleDistanceDiff:	getDataString(rawSummary.totalVehicleDistanceDiff, "distanceTraveled", "Float")
	}
}


// WEBPACK FOOTER //
// ./src/utils/vehiclePOICrossing.js